/**
 * Product gallery carousel adaptive height
 *
 * Localized helper for native <scroll-carousel adaptive-height>. Animates
 * max-height between cells of different aspect ratios as the selection changes.
 * Desktop grids (scroll-snap: none) clear max-height so cells are not clipped.
 */

(function () {
  'use strict';

  var HEIGHT_ANIMATION_DURATION = 180;
  var HEIGHT_ANIMATION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var carouselStates = new WeakMap();

  function prefersReducedMotion() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function cancelHeightAnimation(record) {
    if (!record || !record.heightAnimation) return;

    try {
      record.heightAnimation.cancel();
    } catch (err) {
      /* noop */
    }
    record.heightAnimation = null;
    record.targetHeight = null;
  }

  function selectedCell(carousel) {
    if (carousel.selectedCell) return carousel.selectedCell;
    return (
      carousel.querySelector('.product-gallery__media.is-initial:not([hidden])') ||
      carousel.querySelector('.product-gallery__media.is-initial') ||
      carousel.querySelector('.product-gallery__media:not([hidden])')
    );
  }

  function canAdaptHeight(carousel) {
    var productGallery = carousel.closest('product-gallery');
    var desktopLayout = productGallery
      ? productGallery.getAttribute('data-desktop-layout') || ''
      : '';
    var isDesktop = window.matchMedia && window.matchMedia('(min-width: 1000px)').matches;

    return !(isDesktop && desktopLayout.indexOf('grid') !== -1);
  }

  function updateHeight(carousel, cell) {
    var record = carouselStates.get(carousel);

    if (!canAdaptHeight(carousel)) {
      cancelHeightAnimation(record);
      carousel.style.removeProperty('max-height');
      if (record) record.heightReady = false;
      return;
    }

    var target = cell || selectedCell(carousel);
    if (!target) return;

    var currentHeight = carousel.getBoundingClientRect().height;
    var targetHeight = target.getBoundingClientRect().height;
    if (targetHeight <= 0) return;

    // carousel:change fires before the smooth horizontal scroll begins. If the
    // matching carousel:settle arrives while this animation is still running,
    // keep the existing animation instead of restarting it after the scroll.
    if (
      record &&
      record.heightAnimation &&
      record.targetHeight !== null &&
      Math.abs(record.targetHeight - targetHeight) < 1
    ) {
      return;
    }

    if (
      !record ||
      !record.heightReady ||
      prefersReducedMotion() ||
      typeof carousel.animate !== 'function' ||
      Math.abs(currentHeight - targetHeight) < 1
    ) {
      cancelHeightAnimation(record);
      carousel.style.maxHeight = targetHeight + 'px';
      if (record) record.heightReady = true;
      return;
    }

    cancelHeightAnimation(record);
    carousel.style.maxHeight = targetHeight + 'px';

    var animation = carousel.animate(
      [
        { maxHeight: currentHeight + 'px' },
        { maxHeight: targetHeight + 'px' },
      ],
      {
        duration: HEIGHT_ANIMATION_DURATION,
        easing: HEIGHT_ANIMATION_EASING,
      },
    );
    record.heightAnimation = animation;
    record.targetHeight = targetHeight;

    var clearAnimation = function () {
      if (record.heightAnimation === animation) {
        record.heightAnimation = null;
        record.targetHeight = null;
      }
    };
    animation.addEventListener('finish', clearAnimation, { once: true });
    animation.addEventListener('cancel', clearAnimation, { once: true });
  }

  function teardownCarousel(carousel) {
    var record = carouselStates.get(carousel);
    if (!record) return;
    var productGallery = carousel.closest('product-gallery');

    if (record.observer) {
      try {
        record.observer.disconnect();
      } catch (err) {
        /* noop */
      }
    }
    cancelHeightAnimation(record);
    if (record.onSettle) {
      carousel.removeEventListener('carousel:settle', record.onSettle, true);
    }
    if (record.onChange) {
      carousel.removeEventListener('carousel:change', record.onChange, true);
    }
    carousel.style.removeProperty('max-height');
    if (productGallery) productGallery.classList.remove('is-product-gallery-carousel-ready');
    carouselStates.delete(carousel);
  }

  function setupCarousel(carousel) {
    if (!carousel || !carousel.hasAttribute('adaptive-height')) return;
    if (carouselStates.has(carousel)) return;
    if (typeof ResizeObserver === 'undefined') return;

    var record = {
      heightAnimation: null,
      heightReady: false,
      observer: null,
      onChange: null,
      onSettle: null,
      targetHeight: null,
    };
    carouselStates.set(carousel, record);

    var observer = new ResizeObserver(function () {
      updateHeight(carousel);
    });
    record.observer = observer;

    Array.prototype.forEach.call(
      carousel.querySelectorAll('.product-gallery__media'),
      function (cell) {
        observer.observe(cell);
      },
    );

    var onSettle = function (event) {
      updateHeight(carousel, event.detail && event.detail.cell);
    };
    var onChange = function (event) {
      updateHeight(carousel, event.detail && event.detail.cell);
    };
    carousel.addEventListener('carousel:change', onChange, { capture: true });
    record.onChange = onChange;

    // Capture runs before the native ScrollCarousel bubble listener replaces max-height.
    carousel.addEventListener('carousel:settle', onSettle, { capture: true });
    record.onSettle = onSettle;

    updateHeight(carousel);

    var productGallery = carousel.closest('product-gallery');
    if (productGallery) productGallery.classList.add('is-product-gallery-carousel-ready');
  }

  function getCarousels(root) {
    var scope = root || document;
    if (!scope.querySelectorAll) return [];
    if (
      scope.matches &&
      scope.matches('scroll-carousel.product-gallery__carousel[adaptive-height]')
    ) {
      return [scope];
    }
    return Array.prototype.slice.call(
      scope.querySelectorAll('scroll-carousel.product-gallery__carousel[adaptive-height]'),
    );
  }

  function init(root) {
    getCarousels(root).forEach(setupCarousel);
  }

  function teardown(root) {
    getCarousels(root).forEach(teardownCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });

  // Full product replacements do not emit shopify:section:load. Keep the new
  // gallery on the same adaptive-height contract after its subtree is replaced.
  document.addEventListener('product:rerender', function (event) {
    if (!event.detail || !event.detail.productChange) return;
    var section = event.target.closest('.shopify-section');
    if (!section) return;
    var previousCarousels = getCarousels(section);

    Promise.resolve().then(function () {
      previousCarousels.forEach(function (carousel) {
        if (!carousel.isConnected) teardownCarousel(carousel);
      });
      if (section.isConnected) init(section);
    });
  }, true);
})();
